/**
 * WHOSE TURN IT IS on a track or on one of its rows — the vocabulary, and the one
 * place that decides which member an agent may act on.
 *
 * ## The defect this closes
 *
 * `TrackTask` gained a `nextOwner` column so a row waiting on a person and a row
 * the agent can pick up stop being indistinguishable. That column alone does not
 * close NEX-4713: the complaint is that a board *summed a list it should have
 * split* — "29 ready" over a set in which 23 rows needed a human decision and 6
 * were workable. A field on a row cannot prevent a `.length`. Only the READ can,
 * by refusing to offer one summable list, and this module is the predicate that
 * read partitions on.
 *
 * ## A `Record`, never a `!== "CUE"` test
 *
 * {@link TRACK_NEXT_OWNER_IS_AGENT_WORKABLE} is exhaustive, so a fourth member
 * cannot land without a compile error at the one line that decides what it means.
 * A negated equality would silently classify it as *not workable*, which is the
 * safe direction exactly once — the day somebody adds a member that IS workable,
 * it disappears from `task ready`'s actionable half with nothing red anywhere.
 *
 * 🔴 IT IS AN ACTOR KIND, NEVER A PERSON AND NEVER A PERMISSION, and this module
 * grants and refuses nothing. Access control on tracks is `TrackAccessGuard` +
 * `TrackAccessService.canReach(...)`. Never gate access on a value from here —
 * `TrackTask.nextOwner`'s own docblock in `schema.prisma` carries the three
 * person-shaped columns it is not (`ResourcePermission` + `Relation.OWNER`,
 * `claimedByAgentId`, `doneByUserId`).
 */

/**
 * The vocabulary, default first.
 *
 * 🔴 HAND-WRITTEN, AND DELIBERATELY NOT DERIVED FROM `DbEnum.TrackNextOwner` —
 * the same argument {@link TRACK_TASK_KINDS} makes: the database side has to stay
 * an independent statement for anything to be checked against.
 *
 * ⚠️ ONE ENUM SERVES BOTH GRAINS. `Track.nextOwner` and `TrackTask.nextOwner`
 * share `TrackNextOwner` in `schema.prisma` on purpose, and that coupling is the
 * price of one vocabulary: `ALTER TYPE ... ADD VALUE` is irreversible and widens
 * both columns at once. So a member added here means something at BOTH grains and
 * the two `Record`s below have to be read together.
 */
export const TRACK_NEXT_OWNERS = ["CUE", "USER", "EVENT"] as const;

export type TrackNextOwner = (typeof TRACK_NEXT_OWNERS)[number];

/**
 * Whose turn it is on a TASK when nobody said.
 *
 * 🔴 `CUE` RESTATES TODAY'S BEHAVIOUR AND IS NOT A VERDICT. Before the column
 * existed, `findReadyTasks` offered every undone unblocked work leaf to the agent
 * with no owner predicate at all — so `CUE` is what every pre-existing row already
 * means, and the day the partition landed the workable half moved by zero rows.
 * `USER` would have been the opposite: a claim about content nobody computed,
 * applied to every row at once, emptying every board's workable half.
 *
 * ⚠️ `Track.nextOwner` DEFAULTS TO `USER` AND THAT IS NOT AN INCONSISTENCY. The
 * track-level column is an OPT-IN filter — `findReadyTracks` merely PROJECTS it
 * and `list-tracks` narrows only on `?nextOwner=` — so its default can never
 * remove a row from a read nobody filtered. The task-level column decides what
 * every unfiltered caller of `task ready` sees, which is a consequence the
 * track-level one does not carry.
 */
export const DEFAULT_TRACK_TASK_NEXT_OWNER: TrackNextOwner = "CUE";

/**
 * Which owners mean AN AGENT MAY PICK THIS UP.
 *
 * 🔴 THIS IS THE WHOLE PARTITION, AND IT IS READ BY THREE PLACES THAT MUST AGREE:
 * the `ORDER BY` key in `TrackStore.findReadyTasks`, which sorts the workable half
 * to the front of the page; `ListReadyTrackTasksUseCase`, which splits the rows it
 * gets back; and the CLI's two tables. Spelling `"CUE"` at any of those sites
 * instead of reading this would be three statements of one rule that only drift
 * can distinguish — and a page ordered by one rule and split by another silently
 * truncates the wrong half.
 *
 * ⚠️ `EVENT` IS NOT WORKABLE AND IS NOT "WAITING ON A PERSON" EITHER. It is
 * waiting on something outside — a watcher, a webhook, a deploy. The question this
 * `Record` answers is binary on purpose (*is it the agent's turn?*), and the row
 * still carries its own `nextOwner`, so USER and EVENT stay one field apart for
 * any caller that wants them. A three-way split would make a caller union two sets
 * to recover the binary answer, which is the summing defect in a new spelling.
 */
export const TRACK_NEXT_OWNER_IS_AGENT_WORKABLE: Readonly<Record<TrackNextOwner, boolean>> = {
  CUE: true,
  USER: false,
  EVENT: false
};

/**
 * The single owner value that means AGENT-WORKABLE, for the one consumer that
 * cannot read a `Record`: SQL.
 *
 * `TrackStore.findReadyTasks` sorts on `k."nextOwner"::text = $n` so the workable
 * half reaches the front of the page, and a bound scalar is all a statement can
 * carry — {@link TRACK_NEXT_OWNER_IS_AGENT_WORKABLE} is a TypeScript value the
 * database has never heard of. Binding it rather than writing `'CUE'` in the
 * statement is the same rule the `kind` predicate one line up already follows: a
 * hand-typed literal is a second statement of the vocabulary in a language `tsc`
 * cannot check.
 *
 * 🔴 IT IS NOT {@link DEFAULT_TRACK_TASK_NEXT_OWNER}, THOUGH THE TWO ARE THE SAME
 * STRING TODAY, AND COLLAPSING THEM WOULD TIE TWO UNRELATED DECISIONS TOGETHER.
 * One is *what a row means when nobody said*; this is *which owner an agent may
 * act on*. Changing either for its own reasons must not move the other.
 *
 * 🚨 IT ENCODES AN INVARIANT SQL CANNOT: **exactly one member is workable.** An
 * equality test can only ever sort one value to the front, so the day a second
 * member becomes workable this key silently starves it — the page would order it
 * with the waiting rows and truncate it first, while the TypeScript partition
 * still put it in `workable`. The page and the split would then disagree about the
 * same row, with nothing red. `task-next-owner-partition-agrees-with-the-sort-key`
 * is what refuses that: it asserts this constant is the ONLY `true` member of the
 * `Record`, so widening the `Record` reds rather than drifting.
 */
export const AGENT_WORKABLE_NEXT_OWNER: TrackNextOwner = "CUE";

/**
 * Whether an agent may pick up a row with this owner.
 *
 * ⚠️ `undefined` AND `null` BOTH RESOLVE TO {@link DEFAULT_TRACK_TASK_NEXT_OWNER},
 * so a payload that lost the field crossing a wire — an older client, a partial
 * projection — reads as workable, which is exactly what every row meant before the
 * column existed. The opposite reading would make a dropped field hide real work.
 */
export const isAgentWorkable = (nextOwner: TrackNextOwner | null | undefined): boolean =>
  TRACK_NEXT_OWNER_IS_AGENT_WORKABLE[nextOwner ?? DEFAULT_TRACK_TASK_NEXT_OWNER];
